<div class="hsn-exceptions">
  <header class="page-head">
    <div>
      <h2>HSN Exceptions</h2>
      <p class="sub">
        Products whose HSN code is malformed or not in the official list. These
        are not blocked from saving — fix them here, deliberately.
      </p>
    </div>
    <button class="btn secondary" (click)="load()" [disabled]="loading()">
      {{ loading() ? 'Refreshing…' : 'Refresh' }}
    </button>
  </header>

  <!-- The master gates half of what this page can tell you, so say so loudly
       rather than silently showing a shorter list. -->
  <div
    class="banner warn"
    *ngIf="masterStats() && masterStats()!.total === 0"
  >
    <strong>HSN master not loaded.</strong>
    Only malformed codes can be detected. Codes that are well-formed but do not
    exist — and which of two possible repairs is the real one — cannot be
    determined until the master is imported.
  </div>

  <div
    class="banner info"
    *ngIf="masterStats() as stats"
    [hidden]="stats.total === 0"
  >
    Master holds <strong>{{ stats.total | number }}</strong> codes
    ({{ stats.goods | number }} goods, {{ stats.services | number }} services)
    <span *ngIf="stats.lastImport">
      · last updated
      {{ stats.lastImport.finishedAt | date: 'dd MMM yyyy' }}
      from {{ stats.lastImport.source }}
    </span>
  </div>

  <div class="banner error" *ngIf="error()">{{ error() }}</div>

  <div class="loading" *ngIf="loading()">Loading…</div>

  <ng-container *ngIf="!loading() && report() as data">
    <section class="summary">
      <div class="stat">
        <span class="value">{{ data.summary.affectedProducts | number }}</span>
        <span class="label">products affected</span>
      </div>
      <div class="stat">
        <span class="value">{{ data.summary.affectedCodes | number }}</span>
        <span class="label">distinct codes</span>
      </div>
      <div class="stat">
        <span class="value">{{ data.summary.percentAffected }}%</span>
        <span class="label">of {{ data.summary.activeProducts | number }} active</span>
      </div>
      <div class="stat good" *ngIf="confidentCount() > 0">
        <span class="value">{{ confidentCount() }}</span>
        <span class="label">one-click fixes ready</span>
      </div>
    </section>

    <section class="by-reason" *ngIf="data.summary.byReason">
      <div class="reason" *ngFor="let entry of data.summary.byReason | keyvalue">
        <span class="reason-name">{{ entry.key }}</span>
        <span class="reason-count">
          {{ entry.value.codes }} codes · {{ entry.value.products }} products
        </span>
      </div>
    </section>

    <section class="controls">
      <div class="filters">
        <button
          class="chip"
          [class.active]="filter() === 'ALL'"
          (click)="filter.set('ALL')"
        >
          All ({{ data.rows.length }})
        </button>
        <button
          class="chip"
          [class.active]="filter() === 'FIXABLE'"
          (click)="filter.set('FIXABLE')"
          [disabled]="confidentCount() === 0"
        >
          Ready to fix ({{ confidentCount() }})
        </button>
        <button
          class="chip"
          [class.active]="filter() === 'STRUCTURE'"
          (click)="filter.set('STRUCTURE')"
        >
          Malformed
        </button>
        <button
          class="chip"
          [class.active]="filter() === 'NOT_IN_MASTER'"
          (click)="filter.set('NOT_IN_MASTER')"
        >
          Not in master
        </button>
      </div>
      <input
        class="search"
        type="text"
        placeholder="Filter by code, SKU or product name"
        [ngModel]="search()"
        (ngModelChange)="search.set($event)"
      />
    </section>

    <p class="empty" *ngIf="visibleRows().length === 0">
      Nothing to show. Every HSN code in this filter is clean.
    </p>

    <table class="grid" *ngIf="visibleRows().length > 0">
      <thead>
        <tr>
          <th class="w-code">Stored code</th>
          <th class="w-issue">Issue</th>
          <th class="w-count">Products</th>
          <th class="w-fix">Correction</th>
          <th class="w-action"></th>
        </tr>
      </thead>

      <tbody>
        <ng-container *ngFor="let row of visibleRows()">
          <tr [class.confident]="row.confident">
            <td class="w-code">
              <button class="link code" (click)="toggle(row)">
                {{ row.hsnCode || '(blank)' }}
              </button>
            </td>

            <td class="w-issue">
              <span class="tag" [class.structure]="row.kind === 'STRUCTURE'">
                {{ row.kind === 'STRUCTURE' ? 'Malformed' : 'Not in master' }}
              </span>
              <span class="msg">{{ row.message }}</span>
            </td>

            <td class="w-count">
              <button class="link" (click)="toggle(row)">
                {{ row.productCount | number }}
              </button>
            </td>

            <td class="w-fix">
              <!-- Exactly one repair exists in the master: offer it directly. -->
              <div class="suggest" *ngIf="row.confident">
                <strong>{{ row.suggestions[0].code }}</strong>
                <span class="desc">{{ row.suggestions[0].description }}</span>
              </div>

              <!-- Both repairs exist. We must not choose for the user. -->
              <div class="suggest ambiguous" *ngIf="row.suggestions.length > 1">
                <span class="warn-text">Two possible codes — pick one:</span>
                <label *ngFor="let s of row.suggestions">
                  <input
                    type="radio"
                    [name]="'fix-' + row.hsnCode"
                    [value]="s.code"
                    (change)="manualFix[row.hsnCode] = s.code"
                  />
                  <strong>{{ s.code }}</strong>
                  <span class="desc">{{ s.description }}</span>
                </label>
              </div>

              <!-- Nothing resolvable: manual entry, with a portal lookup link. -->
              <div class="suggest" *ngIf="row.suggestions.length === 0">
                <input
                  class="code-input"
                  type="text"
                  placeholder="Correct HSN"
                  [(ngModel)]="manualFix[row.hsnCode]"
                />
                <a
                  class="link small"
                  [href]="portalUrl(row.normalized)"
                  target="_blank"
                  rel="noopener"
                >
                  Look up on GST portal
                </a>
              </div>
            </td>

            <td class="w-action">
              <button
                class="btn primary small"
                *ngIf="row.confident"
                [disabled]="isApplying(row)"
                (click)="apply(row, row.suggestions[0].code)"
              >
                {{ isApplying(row) ? 'Applying…' : 'Apply' }}
              </button>
              <button
                class="btn small"
                *ngIf="!row.confident"
                [disabled]="isApplying(row) || !manualFix[row.hsnCode]"
                (click)="apply(row, manualFix[row.hsnCode])"
              >
                {{ isApplying(row) ? 'Applying…' : 'Apply' }}
              </button>
            </td>
          </tr>

          <tr class="row-msg" *ngIf="messageFor(row)">
            <td colspan="5">{{ messageFor(row) }}</td>
          </tr>

          <tr class="detail" *ngIf="isExpanded(row)">
            <td colspan="5">
              <div class="products">
                <div class="products-head">
                  Products using {{ row.hsnCode || '(blank)' }}
                  <span *ngIf="row.sampleProducts.length < row.productCount">
                    — showing {{ row.sampleProducts.length }} of
                    {{ row.productCount }}
                  </span>
                </div>
                <ul>
                  <li *ngFor="let p of row.sampleProducts">
                    <span class="sku">{{ p.sku }}</span>
                    <span class="name">{{ p.name }}</span>
                  </li>
                </ul>
              </div>
            </td>
          </tr>
        </ng-container>
      </tbody>
    </table>
  </ng-container>
</div>
